import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  CheckCircle2,
  XCircle,
  Rocket,
  User,
  Mail,
  Building2,
  Calendar,
  Sparkles,
  Loader2,
} from 'lucide-react';
import axios from 'axios';
import './WelcomePage.css';

// Map the Fluent Badge color names (still returned by getStatusBadge below) onto shadcn
// Badge classes, preserving the status color SEMANTICS after the migration:
// success = emerald, warning = amber, danger = red, informative/subtle = neutral gray,
// brand = accent (blue). Mirrors the helper introduced by the U9 migration.
const BADGE_CLASS: Record<string, string> = {
  success: 'border-transparent bg-gold/15 text-foreground',
  warning: 'border-transparent bg-warning/10 text-warning',
  danger: 'border-transparent bg-destructive/10 text-destructive',
  brand: 'border-transparent bg-secondary text-foreground',
  informative: 'border-transparent bg-muted text-text-secondary',
  subtle: 'border-transparent bg-muted text-muted-foreground',
};

function badgeClass(color: string): string {
  return BADGE_CLASS[color] || BADGE_CLASS.informative;
}

interface SubscriptionInfo {
  subscriptionId: string;
  planId: string;
  quantity: number;
  status: string;
  email?: string;
}

type WelcomeStep = 'loading' | 'review' | 'activating' | 'success' | 'error';

function WelcomePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [step, setStep] = useState<WelcomeStep>('loading');
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activationResult, setActivationResult] = useState<any>(null);

  useEffect(() => {
    // Parse query params from marketplace redirect
    const subscriptionId = searchParams.get('subscription_id');
    const planId = searchParams.get('plan_id');
    const quantity = searchParams.get('quantity');
    const status = searchParams.get('status');
    const email = searchParams.get('email');
    const errorParam = searchParams.get('error');
    const errorMessage = searchParams.get('message');

    // Check for error redirect
    if (errorParam) {
      setError(errorMessage || 'An error occurred during marketplace activation');
      setStep('error');
      return;
    }

    // Validate required params
    if (!subscriptionId || !planId) {
      setError('Missing subscription information. Please try again from Azure Marketplace.');
      setStep('error');
      return;
    }

    setSubscriptionInfo({
      subscriptionId,
      planId,
      quantity: parseInt(quantity || '1', 10),
      status: status || 'pending',
      email: email || undefined,
    });
    setStep('review');
  }, [searchParams]);

  const handleActivate = async () => {
    if (!subscriptionInfo) return;

    setStep('activating');

    try {
      const response = await axios.post('/api/v1/marketplace/activate', {
        subscriptionId: subscriptionInfo.subscriptionId,
        planId: subscriptionInfo.planId,
        quantity: subscriptionInfo.quantity,
      });

      setActivationResult(response.data);
      setStep('success');
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.message || err.message || 'Activation failed');
      } else {
        setError('An unexpected error occurred');
      }
      setStep('error');
    }
  };

  const getPlanDisplayName = (planId: string) => {
    const planNames: Record<string, string> = {
      'parslee-agent': 'ButtonUp AI Employees',
      'parslee-agents-standard': 'ButtonUp AI Employees - Standard',
      'parslee-agents-professional': 'ButtonUp AI Employees - Professional',
      'parslee-agents-enterprise': 'ButtonUp AI Employees - Enterprise',
    };
    return planNames[planId] || planId;
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; label: string }> = {
      pending: { color: 'warning', label: 'Pending Activation' },
      pendingfulfillment: { color: 'warning', label: 'Pending Activation' },
      subscribed: { color: 'success', label: 'Active' },
      active: { color: 'success', label: 'Active' },
    };
    const config = statusConfig[status.toLowerCase()] || { color: 'informative', label: status };
    return <Badge variant="outline" className={badgeClass(config.color)}>{config.label}</Badge>;
  };

  if (step === 'loading') {
    return (
      <div className="welcome-container">
        <div className="welcome-loading">
          <Loader2 className="h-8 w-8 animate-spin text-accent-text" />
          <span>Loading subscription details...</span>
        </div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="welcome-container">
        <div className="welcome-card error-card">
          <div className="welcome-header">
            <XCircle className="error-icon" />
            <h1 className="text-3xl font-semibold">Activation Error</h1>
          </div>
          <p className="error-message text-sm">{error}</p>
          <div className="welcome-actions">
            <Button variant="default" onClick={() => window.location.href = 'https://portal.azure.com'}>
              Return to Azure Portal
            </Button>
            <Button variant="secondary" onClick={() => window.location.href = 'mailto:support@buttonup.ai'}>
              Contact Support
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    // Redirect to getting started page with activation info
    const gettingStartedUrl = `/getting-started?email=${encodeURIComponent(subscriptionInfo?.email || '')}&org_id=${encodeURIComponent(activationResult?.organizationId || '')}&quantity=${subscriptionInfo?.quantity || 1}`;

    return (
      <div className="welcome-container">
        <div className="welcome-card success-card">
          <div className="welcome-header">
            <CheckCircle2 className="success-icon" />
            <h1 className="text-3xl font-semibold">Subscription Activated!</h1>
          </div>
          <p className="success-message">
            Your AI Employee subscription has been activated successfully. Let's get you set up!
          </p>

          <div className="activation-details">
            <div className="detail-item">
              <Building2 />
              <div>
                <p className="text-xs text-muted-foreground">Organization ID</p>
                <p className="text-sm">{activationResult?.organizationId}</p>
              </div>
            </div>
            <div className="detail-item">
              <User />
              <div>
                <p className="text-xs text-muted-foreground">AI Employees</p>
                <p className="text-sm">{activationResult?.quantity} seat(s) activated</p>
              </div>
            </div>
          </div>

          <div className="welcome-actions">
            <Button
              variant="default"
              size="lg"
              onClick={() => navigate(gettingStartedUrl)}
            >
              <Rocket />
              Continue to Setup Guide
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'activating') {
    return (
      <div className="welcome-container">
        <div className="welcome-card">
          <div className="welcome-header">
            <Loader2 className="h-8 w-8 animate-spin text-accent-text" />
            <h1 className="text-3xl font-semibold">Activating Your Subscription</h1>
          </div>
          <p className="text-sm">Please wait while we set up your AI Employees...</p>
          <div className="activation-progress h-1 w-full overflow-hidden rounded-full bg-secondary">
            <div className="h-full w-1/3 rounded-full bg-primary animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  // Review step
  return (
    <div className="welcome-container">
      <div className="welcome-card">
        <div className="welcome-header">
          <div className="welcome-brand">
            <div className="welcome-brand-icon">
              <Sparkles />
            </div>
            <span className="welcome-brand-text">ButtonUp</span>
          </div>
          <h1 className="text-3xl font-semibold">Welcome to ButtonUp AI Employees</h1>
        </div>

        <p className="welcome-intro text-sm">
          Thank you for choosing Parslee! Please review your subscription details below and click "Activate" to complete your setup.
        </p>

        <hr className="welcome-divider" />

        <h2 className="text-[1.75rem] font-semibold">Subscription Details</h2>

        <div className="subscription-details">
          <div className="detail-row">
            <div className="detail-item">
              <Building2 />
              <div>
                <p className="text-xs text-muted-foreground">Plan</p>
                <p className="text-sm">{getPlanDisplayName(subscriptionInfo?.planId || '')}</p>
              </div>
            </div>
            <div className="detail-item">
              <User />
              <div>
                <p className="text-xs text-muted-foreground">AI Employees</p>
                <p className="text-sm">{subscriptionInfo?.quantity} seat(s)</p>
              </div>
            </div>
          </div>

          <div className="detail-row">
            {subscriptionInfo?.email && (
              <div className="detail-item">
                <Mail />
                <div>
                  <p className="text-xs text-muted-foreground">Email</p>
                  <p className="text-sm">{subscriptionInfo.email}</p>
                </div>
              </div>
            )}
            <div className="detail-item">
              <Calendar />
              <div>
                <p className="text-xs text-muted-foreground">Status</p>
                {getStatusBadge(subscriptionInfo?.status || 'pending')}
              </div>
            </div>
          </div>
        </div>

        <div className="pricing-info">
          <h3 className="text-2xl font-semibold">Pricing</h3>
          <div className="price-breakdown">
            <div className="price-line">
              <p className="text-sm">AI Employee Seat</p>
              <p className="text-sm">$500/month</p>
            </div>
            <div className="price-line">
              <p className="text-sm">Quantity</p>
              <p className="text-sm">x {subscriptionInfo?.quantity}</p>
            </div>
            <hr />
            <div className="price-line total">
              <p className="text-sm">Monthly Total</p>
              <h3 className="text-2xl font-semibold">${(subscriptionInfo?.quantity || 1) * 500}/month</h3>
            </div>
          </div>
          <p className="pricing-note">
            Includes 5M tokens per AI Employee. Additional usage billed at market rates.
          </p>
        </div>

        <hr className="welcome-divider" />

        <div className="terms-section">
          <p className="text-sm text-muted-foreground">
            By clicking "Activate", you agree to the{' '}
            <a href="https://buttonup.ai/terms" target="_blank" rel="noopener noreferrer">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="https://buttonup.ai/privacy" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>.
          </p>
        </div>

        <div className="welcome-actions">
          <Button
            variant="default"
            size="lg"
            onClick={handleActivate}
          >
            <Rocket />
            Activate Subscription
          </Button>
          <Button
            variant="secondary"
            onClick={() => window.location.href = 'https://portal.azure.com'}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

export default WelcomePage;
